import { useEffect, useState, useMemo } from "react";
import { usePurchaseVoucher } from "../context/PurchaseVoucherContext";
import { Plus, Pencil, Trash2, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Pagination, { usePagination } from '../shared/Pagination';

const fmt = (n) => `₹ ${Number(n).toLocaleString("en-IN")}`;

export default function PurchaseVouchers() {
  const navigate = useNavigate();
  const {
    purchaseVouchers,
    fetchPurchaseVouchers,
    deletePurchaseVoucher,
    loading,
  } = usePurchaseVoucher();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { fetchPurchaseVouchers(); }, []);

  const data = useMemo(
    () => (Array.isArray(purchaseVouchers) ? purchaseVouchers : []),
    [purchaseVouchers]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let result = data.filter((item) =>
      item.customername?.toLowerCase().includes(q) ||
      item.note?.toLowerCase().includes(q)
    );
    if (sortBy === "amount_desc") result = [...result].sort((a, b) => Number(b.amount) - Number(a.amount));
    if (sortBy === "amount_asc")  result = [...result].sort((a, b) => Number(a.amount) - Number(b.amount));
    if (sortBy === "name_asc")    result = [...result].sort((a, b) => a.customername?.localeCompare(b.customername));
    return result;
  }, [data, search, sortBy]);

  const {
    page,
    pageSize,
    setPage,
    setPageSize,
    totalPages,
    totalItems,
    paginated,
  } = usePagination(filtered, 10);

  useEffect(() => { setPage(1); }, [search, sortBy]);

  const withBalance = data.filter(i => Number(i.balance) > 0).length;
  const cleared     = data.filter(i => Number(i.balance) === 0).length;

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete voucher for "${name}"? This cannot be undone.`)) return;
    try {
      setDeletingId(id);
      await deletePurchaseVoucher(id);
    } catch (err) {
      alert(err.message || "Failed to delete voucher");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {[
            { label:"Total Vouchers",  value:data.length,   icon:'📋', accent:'border-l-teal-500',  color:'text-teal-700'  },
            { label:"With Balance",    value:withBalance,   icon:'💰', accent:'border-l-green-500', color:'text-green-600' },
            { label:"Cleared",         value:cleared,       icon:'✅', accent:'border-l-gray-400',  color:'text-gray-600'  },
          ].map(({ label, value, accent, color }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
              <div><p className={`text-xl font-bold ${color}`}>{value}</p><p className="text-xs text-gray-400 leading-tight">{label}</p></div>
            </div>
          ))}
        </div>

        {/* Search & Sort */}
        <div className="flex flex-col sm:flex-row gap-3 bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
          <input
            type="text"
            placeholder="Search voucher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 h-10 px-4 rounded-lg border border-gray-200 bg-gray-50 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 placeholder-gray-400"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-10 px-4 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500 outline-none cursor-pointer focus:border-teal-400 sm:min-w-[160px]"
          >
            <option value="">Sort By</option>
            <option value="amount_desc">Amount: High → Low</option>
            <option value="amount_asc">Amount: Low → High</option>
            <option value="name_asc">Name: A → Z</option>
          </select>
          <button onClick={() => navigate("/create-purchase")}
            className="flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 h-10 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0">
            <Plus size={15} /><span className="hidden sm:inline">Add Voucher</span>
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-teal-700 text-white">
                <tr>
                  {["ID", "Supplier", "Amount", "Paid", "Balance", "Note", "Actions"].map((col) => (
                    <th key={col}
                      className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-white
                        ${["Amount","Paid","Balance"].includes(col) ? "text-right" : ""}
                        ${col === "Actions" ? "text-center" : ""}
                      `}
                    >{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-14 text-gray-400">Loading…</td></tr>
                ) : paginated.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-14 text-gray-400">No records found</td></tr>
                ) : paginated.map((item, idx) => (
                  <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-gray-400 text-xs font-mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td className="px-6 py-4 font-semibold text-gray-800">{item.customername}</td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-700">{fmt(item.amount)}</td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-700">{fmt(item.paid)}</td>
                    <td className="px-6 py-4 text-right font-semibold text-red-500">{fmt(item.balance)}</td>
                    <td className="px-6 py-4 text-gray-400 max-w-[180px] truncate">{item.note || "—"}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => navigate(`/edit-purchase/${item.id}`)}
                          className="text-blue-400 hover:text-blue-600 transition-colors"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id, item.customername)}
                          disabled={deletingId === item.id}
                          className="text-red-400 hover:text-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-50">
            {loading ? (
              <div className="text-center py-14 text-gray-400 text-sm">Loading…</div>
            ) : paginated.length === 0 ? (
              <div className="text-center py-14 text-gray-400 text-sm">No records found</div>
            ) : paginated.map((item, idx) => (
              <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{item.customername}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{(page - 1) * pageSize + idx + 1}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => navigate(`/edit-purchase/${item.id}`)}
                      className="text-blue-400 hover:text-blue-600 p-1.5"
                      title="Edit"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.customername)}
                      disabled={deletingId === item.id}
                      className="text-red-400 hover:text-red-600 p-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gray-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-[10px] text-gray-400">Amount</p>
                    <p className="text-xs font-bold text-gray-700">{fmt(item.amount)}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-[10px] text-gray-400">Paid</p>
                    <p className="text-xs font-bold text-green-600">{fmt(item.paid)}</p>
                  </div>
                  <div className="bg-red-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-[10px] text-gray-400">Balance</p>
                    <p className="text-xs font-bold text-red-500">{fmt(item.balance)}</p>
                  </div>
                </div>
                {item.note && <p className="text-xs text-gray-400 mt-2 truncate">Note: {item.note}</p>}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="border-t border-gray-100">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          )}
        </div>

      </div>
    </main>
  );
}